import React from 'react';
import { Link } from 'react-router-dom';
import {
  useRessourcesRecommendations,
  DossierRecommendation,
  HalRecommendation,
} from '../../../hooks/useRessourcesRecommendations';
import useViewport from '../../../hooks/useViewport';

const RessourcesSidebar = () => {
  const { dossiers, halResources, loading, error } = useRessourcesRecommendations(4);
  const { isMobile } = useViewport();

  const containerClass = isMobile ? 'w-full' : 'w-80';
  const panelClass =
    'bg-mcdm-primary-200 dark:bg-mcdm-third-400 border border-white dark:border-mcdm-third-400 rounded-lg p-4';

  if (loading) {
    return (
      <div className={containerClass}>
        <div className={panelClass}>
          <h2 className="text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi text-lg mb-4">
            Ressources recommandées
          </h2>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-mcdm-third-300 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <div className={panelClass}>
          <h2 className="text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi text-lg mb-4">
            Ressources recommandées
          </h2>
          <p className="text-red-500 dark:text-red-400 text-sm">
            Une erreur est survenue lors du chargement des recommandations
          </p>
        </div>
      </div>
    );
  }

  const hasDossiers = dossiers.length > 0;
  const hasHal = halResources.length > 0;

  if (!hasDossiers && !hasHal) {
    return (
      <div className={containerClass}>
        <div className={panelClass}>
          <h2 className="text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi text-lg mb-3">
            Ressources recommandées
          </h2>
          <p className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-200">
            Pas encore de suggestion. Affinez vos centres d'intérêt dans votre profil pour voir apparaître des dossiers
            publics correspondant à vos thématiques.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={panelClass}>
        <h2 className="text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi text-lg mb-4">
          Ressources recommandées
        </h2>

        {hasDossiers && (
          <div className="space-y-3 mb-4">
            <h3 className="text-xs uppercase tracking-wide text-mcdm-third-400 dark:text-mcdm-primary-300">
              Dossiers de la communauté
            </h3>
            {dossiers.map((d) => (
              <DossierCard key={d.id} dossier={d} />
            ))}
          </div>
        )}

        {hasHal && (
          <div className="space-y-3">
            <h3 className="text-xs uppercase tracking-wide text-mcdm-third-400 dark:text-mcdm-primary-300 mt-2">
              Publications académiques
            </h3>
            {halResources.map((r) => (
              <HalCard key={r._id} resource={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DossierCard: React.FC<{ dossier: DossierRecommendation }> = ({ dossier }) => (
  <Link
    to={`/dossiers/${dossier.id}`}
    className="block p-3 bg-mcdm-primary-100 dark:bg-mcdm-third-300 rounded-lg hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-200 transition-colors"
  >
    <h4 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-1 line-clamp-2">
      {dossier.title}
    </h4>
    <div className="flex items-center gap-2 text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
      <span className="truncate">{dossier.domain}</span>
      {dossier.authorName && (
        <>
          <span aria-hidden>·</span>
          <span className="truncate">{dossier.authorName}</span>
        </>
      )}
    </div>
  </Link>
);

const HalCard: React.FC<{ resource: HalRecommendation }> = ({ resource }) => {
  const url = resource.source?.url;
  const inner = (
    <>
      <h4 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-1 line-clamp-2">
        {resource.titre}
      </h4>
      <div className="flex items-center text-xs text-mcdm-third-400 dark:text-mcdm-primary-200 space-x-3">
        <span>{resource.halMetadata?.publication?.type || 'Publication'}</span>
        {resource.halMetadata?.publication?.date && (
          <span>{new Date(resource.halMetadata.publication.date).getFullYear()}</span>
        )}
      </div>
    </>
  );
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block p-3 bg-mcdm-primary-100 dark:bg-mcdm-third-300 rounded-lg hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-200 transition-colors"
      >
        {inner}
      </a>
    );
  }
  return <div className="p-3 bg-mcdm-primary-100 dark:bg-mcdm-third-300 rounded-lg">{inner}</div>;
};

export default RessourcesSidebar;
